"use client";
/* 通用国家选择组件 */
// React
import React, { useEffect, useState, forwardRef } from "react";
// 三方库
import { useTranslation } from "@/i18n/client";
// 公共方法
import { queryGlobalCountriesData } from "@/utils/countryUtils";
import { userSwitchCountry } from "@/utils/multiSite";
// 接口
import { Country } from "@/apis/address/address.interface";
// 组件
import { Select, MenuItem, Skeleton } from "@mui/material";
import SiteChangeConfirmDialog from "@/components/countrySelect/SiteChangeConfirmDialog";
import ConfirmDialog from "@/components/countrySelect/CountryChangeConfirmDialog";
// 资源
import CheckIcon from "@/assets/icons/Checkbox.svg";
import UpArrowIcon from "@/assets/icons/address/UpArrowIcon.svg";
import DownArrowIcon from "@/assets/icons/address/DownArrowIcon.svg";
// 样式
import styles from "./index.module.scss";

export interface CountrySelectRef {
}

interface CountrySelectProps {
  value?: string; // 默认选中国家
  className?: string;
  width?: string | number;
  canSwitchSite?: boolean; // 是否可以切换站点 默认可以切换
  canSwitchCountry?: boolean; // 是否可以切换国家 默认可以切换
  autoUpdate?: boolean; // 回显数据是否自动更新 默认true
  onChange?: (country: Country) => void; // 更新国家数据回调
  confirmNonCrossSite?: boolean; // 是否对非跨站点切换进行二次确认。当为true时，即使是非跨站点的国家切换也会显示确认弹窗。
}

const CountrySelect = forwardRef<CountrySelectRef, CountrySelectProps>(({
  value,
  className,
  width,
  canSwitchSite = true, 
  canSwitchCountry = true,
  autoUpdate = true,
  onChange,
  confirmNonCrossSite = false,
}, ref) => {
  const { t } = useTranslation();
  
  const [countryList, setCountryList] = useState<Country[]>([]); // 国家列表
  const [currentCountryCode, setCurrentCountryCode] = useState<string>(value ?? ""); // 当前选择国家编码
  const [currentCountryData, setCurrentCountryData] = useState<Country | undefined>(); // 当前选择国家详细数据
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false); // 下拉框是否展开
  const [showSiteChangeDialog, setShowSiteChangeDialog] = useState(false); // 控制跨站点确认弹窗显示
  const [showConfirmDialog, setShowConfirmDialog] = useState(false); // 控制非跨站点确认弹窗显示
  const [otherSiteCountryInfo, setOtherSiteCountryInfo] = useState<Country | null>(null);
  const [selectedCountryInfo, setSelectedCountryInfo] = useState<Country | null>(null);
  const [loadingFlags, setLoadingFlags] = useState<Record<string, boolean>>({});
  const [selectedFlagLoading, setSelectedFlagLoading] = useState<boolean>(true);

  useEffect(() => {
    initCountryData();
  }, [canSwitchSite]);

  useEffect(() => {
    if (value !== undefined) {
      setCurrentCountryCode(value);
    }
  }, [value]);

  const initCountryData = async () => {
    try {
      const { countryList: countryListData, currentCountry, currentCountryCode: code } = 
        await queryGlobalCountriesData({
          value,
        });
      
      const filterList = countryListData?.filter(country => country?.siteId === currentCountry?.siteId);

      // 设置国家列表和当前选中的国家
      setCountryList(canSwitchSite ? countryListData : filterList);
      if (value === undefined) {
        setCurrentCountryCode(code);
      }
      currentCountry && setCurrentCountryData(currentCountry);
    } catch (error) {
      console.error("初始化国家数据异常:", error);
    }
  };

  const handleCountryChange = (event: any) => {
    const fromCountry = currentCountryCode
    const toCountry = event.target.value;
    const selectedCountry = countryList.find(
      (country) => country.countryCode === toCountry
    );
    const isOtherSite = currentCountryData?.siteId !== selectedCountry?.siteId;
    
    if (selectedCountry) {
      if(onChange) {
        // 外部传回调 组件内不做处理
        if (autoUpdate) {
          setCurrentCountryData(selectedCountry);
          setCurrentCountryCode(toCountry);  
        }
        onChange?.(selectedCountry);
        return;
      }

      if (isOtherSite) {
        // 跨站点二次确认
        setShowSiteChangeDialog(true);
        setOtherSiteCountryInfo(selectedCountry)
      } else if (confirmNonCrossSite) {
        // 非跨站点二次确认
        setShowConfirmDialog(true);
        setSelectedCountryInfo(selectedCountry);
      } else {
        setCurrentCountryData(selectedCountry);
        setCurrentCountryCode(toCountry);    
        userSwitchCountry(toCountry);
      }
    }
  };

  const handleConfirmChange = () => {
    if (selectedCountryInfo) {
      setCurrentCountryData(selectedCountryInfo);
      setCurrentCountryCode(selectedCountryInfo.countryCode);
      userSwitchCountry(selectedCountryInfo.countryCode);
    }
    setShowConfirmDialog(false);
  };

  // 国家列表为空 不渲染组件
  if (countryList.length === 0) {
    return <div></div>;
  }

  return (
    <div 
      className={`${styles.countrySelectWrapper} ${className || ''}`} 
      style={width ? { width } : undefined}
    >
      <Select
        value={value !== undefined ? value : currentCountryCode}
        onChange={handleCountryChange}
        disabled={!canSwitchCountry}
        fullWidth
        sx={{
          height: "40px",
          "& .MuiSelect-select": {
            height: "40px",
            padding: "0 16px",
            display: "flex",
            alignItems: "center"
          }
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 250,
            },
          },
          sx: {
            "& .MuiMenuItem-root": {
              height: "44px",
              minHeight: "44px",
              maxHeight: "44px",
              padding: "0 16px",
              boxSizing: "border-box",
              overflow: "hidden"
            },
            "& .MuiMenuItem-root.Mui-selected": {
              backgroundColor: "#FFFFFF !important"
            }
          }
        }}
        IconComponent={() => (
          <>
            {isDropdownOpen ? (<UpArrowIcon className={styles.dropdownIcon} />) : (<DownArrowIcon className={styles.dropdownIcon} />)}
          </>
        )}
        onOpen={() => {
          setIsDropdownOpen(true);
          // 重置图片加载状态
          setLoadingFlags({});
        }}
        onClose={() => setIsDropdownOpen(false)}
        renderValue={(selected) => {
          const country = countryList.find(
            (item) => item.countryCode === selected
          );
          return (
            <div className={styles.countryItem}>
              {country?.flagImage && (
                <div className={styles.flagContainer}>
                  <Skeleton 
                    variant="rectangular"
                    animation="wave" 
                    className={!selectedFlagLoading ? styles.hidden : styles.flagSkeleton} 
                  />
                  <img 
                    src={country.flagImage} 
                    alt={country.name} 
                    className={`${styles.countryFlag} ${selectedFlagLoading ? styles.hidden : ''}`}
                    onLoad={() => setSelectedFlagLoading(false)}
                    onError={() => setSelectedFlagLoading(false)}
                  />
                </div>
              )}
              <span className={styles.countryName}>{country?.name}</span>
            </div>
          );
        }}
      >
        {countryList.map((country) => (
          <MenuItem 
            className={styles.countrySelectWrapper}
            value={country.countryCode} 
            key={country.countryCode}
          >
            <div 
              className={styles.selectedItem}
            >
              <div 
                className={styles.itemContent}
              >
                {country.flagImage && (
                  <div className={styles.flagContainer}>
                    <Skeleton 
                      variant="rectangular"
                      animation="wave"
                      className={loadingFlags[country.countryCode] ? styles.hidden : styles.flagSkeleton} 
                    />
                    <img 
                      src={country.flagImage} 
                      alt={country.name} 
                      className={`${styles.countryFlag} ${loadingFlags[country.countryCode] ? '' : styles.hidden}`}
                      onLoad={() => setLoadingFlags(prev => ({ ...prev, [country.countryCode]: true }))}
                      onError={() => setLoadingFlags(prev => ({ ...prev, [country.countryCode]: true }))}
                    />
                  </div>
                )}
                <span className={styles.countryName}>{country.name}</span>
              </div>
              {country.countryCode === currentCountryCode && <CheckIcon />}
            </div>
          </MenuItem>
        ))}
      </Select>

      {/* 跨站点确认弹窗 */}
      <SiteChangeConfirmDialog
        open={showSiteChangeDialog}
        toCountryCode={otherSiteCountryInfo?.countryCode || ""}
        toCountryName={otherSiteCountryInfo?.name || ""}
        setDialogOpen={setShowSiteChangeDialog}
      />

      {/* 非跨站点确认弹窗 */}
      <ConfirmDialog
        open={showConfirmDialog}
        onConfirm={handleConfirmChange}
        onCancel={() => setShowConfirmDialog(false)}
      />
    </div>
  );
});

CountrySelect.displayName = 'CountrySelect';

export default CountrySelect;
